/**
 * Trims the passed in text and adds ellipses.
 *
 * Args:
 *      text (String): Local image path.
 *      maxChars (Number): The max number of characters to allow before adding ellipses.
 *      insertAt (Number): Optional - The string index where the ellipses should be inserted.
 *      token (String): Optional - The token to use instead of "...".
 *
 * Returns:
 *      (String): The trimmed string with ellipses added.
 *
 * Examples:
 *      // outputs "some long ..."
 *      addEllipses({
 *          text: 'some long file name',
 *          maxChars: 10,
 *      })
 *
 *      // outputs "some ... name"
 *      addEllipses({
 *          text: 'some long file name',
 *          maxChars: 10,
 *          insertAt: 5,
 *      })
 *
 *      // outputs "some [...] name"
 *      addEllipses({
 *          text: 'some long file name',
 *          maxChars: 10,
 *          insertAt: 5,
 *          token: '[...]',
 *      })
 */
export function addEllipses({ text, maxChars, insertAt, token = '...' }) {
    if (text.length <= maxChars) return text;
    if (insertAt > maxChars) insertAt = 0;

    const prefix = text.slice(0, insertAt || maxChars);
    const suffix = insertAt ? text.slice(text.length - (maxChars - insertAt)) : '';

    return `${ prefix }${ token }${ suffix }`;
}

/**
 * Gets unit type (e.g. "px", "%", "rem") from CSS value.
 *
 * Args:
 *      value (String): CSS value.
 *
 * Returns:
 *      (String) The CSS's unit type.
 *
 * Example:
 *      getUnitFromCssValue("10px");  // returns: "px"
 */
export function getUnitFromCssValue(value) {
    const unitMatch = String(value).match(/[a-zA-Z%]+/);

    return unitMatch ? unitMatch[0] : '';
}

/**
 * Gets number from CSS value.
 *
 * Args:
 *      value (String): CSS value.
 *
 * Returns:
 *      (Number) The CSS's number (i.e. value without any unit type).
 *
 * Example:
 *      getNumberFromCssValue("10px");  // returns: 10
 */
export function getNumberFromCssValue(value) {
    const unit = getUnitFromCssValue(value);
    const endIndex = unit ? -unit.length : value.length;

    return Number(String(value).slice(0, endIndex));
}

/**
 * Gets RGBA color value from a hex color string
 *
 * N.b. Functions handles both 6-char hex and 3-char hex, with/without '#' prefix
 *
 * Args:
 *      hex (String): Hex color
 *      alpha (Number): Optional alpha transparency value.
 *
 * Returns:
 *      (String) Converted RGBA value; if invalid hex value is given, function returns a transparent color.
 *
 * Example:
 *      getRgbaFromHex("#f3a85e", 0.5);  // returns: "rgba(243, 168, 94, 0.5)"
 */
export function getRgbaFromHex(hex, alpha = 1) {
    // if invalid hex string, return color as 'transparent'
    if (!hex || hex.length > 7) {
        return 'rgba(0, 0, 0, 0)';
    }
    // if shorthand hex notation, simply concat the string by itself:
    if (hex.length <= 4) {
        hex = hex.slice(-3) + hex.slice(-3);
    }

    // partition string into 3 then map hex to number:
    const [r, g, b] = hex.match(/\w\w/g)
        .map(x => parseInt(x, 16));

    return `rgba(${ r }, ${ g }, ${ b }, ${ alpha })`;
}
